import { FC } from 'react';
import { matchPath } from 'react-router-dom';
import { configureScreenHOC } from 'new/routes/configureScreenHOC';
import { Breakpoint } from '@mui/material';
import { isArray } from 'lodash';

export type RouteConfig = {
  path: string;
  exact?: boolean;
  manageBreadcrumbs?: boolean; // if the component manages its own breadcrumbs instead of using the URL-based breadcrumbs
  name: string;
  component: any;
  header?: HeaderConfig;
  pageTitle?: string | { [key: string]: string };
  privateRoute?: boolean;
};

type RoutesConfig = {
  [domain: string]: {
    [screeName: string]: RouteConfig;
  };
};

export type ModalConfig = {
  name: string;
  title?: string;
  fullWidth?: boolean;
  maxWidth?: Breakpoint;
  removeContentPadding?: boolean;
  rootClassName?: string;
  contentClassName?: string;
};

export type ModalComponent = { modalConfig: ModalConfig };

export type HeaderConfig = {
  title?: string;
  slugs?: string[];
  slugTitle?: string;
};

export type ScreenConfig<T> = {
  screenName: string;
  path: string | string[];
  domain: string;
  exact?: boolean;
  manageBreadcrumbs?: boolean; // Component manages its own breadcrumbs instead of using URL-based breadcrumbs
  hideSideBar?: boolean;
  defaultProps?: any;
  modals?: ModalComponent[];
  pageTitle?: string | { [key: string]: string };
  header?: HeaderConfig;
  drawer?: {
    open: boolean;
  };
  // useful if you need to handle AsyncComponents
  component?: ScreenComponent<T>;
  baseConfig?: {
    exact: boolean;
    privateRoute: boolean;
  };
};

export type ScreenComponent<T> = FC<T> & { screenConfig: ScreenConfig<T> };

export class RouterManager {
  _routes: RoutesConfig;

  static parseRoutes<T, S>(screenComponents: ScreenComponent<T>[]): RoutesConfig {
    return screenComponents.reduce(
      (routesConfig, screenComponent) => {
        const { screenConfig } = screenComponent;

        if (screenConfig?.path) {
          const baseConfig: any = screenConfig.baseConfig || {
            exact: true,
            privateRoute: true,
          };

          // Following check is to assign screenConfig to an Async Component
          if (screenConfig?.component && !screenConfig?.component?.screenConfig) {
            screenConfig.component.screenConfig = { ...screenConfig };
            delete screenConfig.component.screenConfig.component;
          }
          baseConfig.exact = screenConfig.exact || false;
          baseConfig.manageBreadcrumbs = screenConfig.manageBreadcrumbs;
          baseConfig.defaultProps = screenConfig.defaultProps || {};
          baseConfig.path = screenConfig.path;
          baseConfig.name = screenConfig.screenName;
          baseConfig.header = screenConfig.header;
          baseConfig.drawer = screenConfig.drawer;
          baseConfig.pageTitle = screenConfig.pageTitle;
          baseConfig.component = configureScreenHOC(
            !!screenConfig?.component ? screenConfig.component : screenComponent,
          );
          if (screenConfig.domain) {
            routesConfig[screenConfig.domain] = routesConfig[screenConfig.domain] || {};
            routesConfig[screenConfig.domain][screenConfig.screenName] = baseConfig;
          } else {
            routesConfig.main[screenConfig.screenName] = baseConfig;
          }
        }

        return routesConfig;
      },
      { main: {} },
    );
  }

  static matchPathWithRoute(pathname: string, route: RouteConfig) {
    const path = pathname.replace(/\/$/g, '');

    if (!route.path) return false;
    if (isArray(route.path)) {
      return route.path.some((o) => {
        if (o.includes(':')) {
          return !!matchPath(path, o);
        }
        return o === path;
      });
    }
    if (route.path.includes(':')) {
      return !!matchPath(path, route.path);
    }

    return route.path === path;
  }

  constructor(...screenComponents: ScreenComponent<unknown>[]) {
    this._routes = RouterManager.parseRoutes(screenComponents);
  }

  getRoutes(domain = 'main') {
    return Object.values(this._routes[domain] || {});
  }

  getRoute(routeName: string, domain = 'main') {
    return this._routes[domain][routeName];
  }

  getPath(routeName: string, domain = 'main', params: Array<any>) {
    const route = this._routes[domain][routeName];

    if (!route) {
      throw new Error(`No route found for name: ${routeName}`);
    }

    if (route.path.includes(':')) {
      const routeSplitted = route.path.split('/');
      let replaceQty = 0;
      for (let i = 0; i < routeSplitted.length; i += 1) {
        if (routeSplitted[i].includes(':')) {
          routeSplitted[i] = params[replaceQty];
          replaceQty += 1;
        }
      }
      return routeSplitted.join('/');
    }

    return route.path;
  }

  getByPathname(pathname: string): RouteConfig {
    const flatRoutes = Object.keys(this._routes).reduce((routes, domain) => {
      routes.push(...Object.values(this._routes[domain]));
      return routes;
    }, []);
    return flatRoutes.find((o) => RouterManager.matchPathWithRoute(pathname, o));
  }

  getRoutesNames(domain = 'main') {
    return Object.keys(this._routes[domain]).reduce((routeNames, routeName) => {
      routeNames[routeName] = routeName;
      return routeNames;
    }, {});
  }
}
